import {
  getLayout,
  saveLayout,
  mergeLayout,
  deleteLayout
} from '../../core/LayoutPersistent';
import { GridApi, ColDef, ColumnApi, GridOptions } from 'ag-grid-community';
import { ref, Ref, nextTick, reactive } from 'vue';
import _ from 'lodash-es';
import { useShareData } from '../../core/SharedData';
import { arrayMove } from '../../core/Common';
import { useAgColumnReactive } from './AgColumnReative';

type PersistColumns = { id: string; show: boolean; width?: number }[];
type PlainObject<T = any> = Record<string, T>;

function distinctPersistColumns(persistColumns: PersistColumns) {
  const result: PersistColumns = [];
  for (const column of persistColumns) {
    if (result.find((_) => _.id == column.id)) {
      continue;
    }
    result.push(column);
  }
  return result;
}

function filterOutNotInColumnData(
  persistColumns: PersistColumns,
  realColDefs: ColDef<any>[]
) {
  const result: PersistColumns = [];
  for (const column of persistColumns) {
    const isIn = realColDefs.find(
      (realColDef) => realColDef.field == column.id
    );
    if (isIn) {
      result.push(column);
    }
  }
  return result;
}

/**
 * 获取本地缓存的列数据
 * 这里有个问题是对列进行排序时是通过索引进行缓存数据做的交换
 * 所以获取缓存数据时需将重复以及不存在的列缓存删除掉 否则会导致索引匹配错误的问题
 */
function getPersistColumns(name: string, realColDefs: ColDef<any>[] = []) {
  const result = getLayout<PersistColumns>(
    'table',
    ['columns', name],
    [],
    (v) => !!v.push
  );
  // 对缓存做去重 从旧版本迁移过来的数据可能会有重复
  const distinctResult = distinctPersistColumns(result);
  if (!realColDefs.length) {
    return distinctResult;
  }
  // 对缓存移除当前配置列中不存在的缓存 新页面会对列做组合 需要将这些被组合的列做删除
  const realResult = filterOutNotInColumnData(distinctResult, realColDefs);
  return realResult;
}

type RseAgColumnReactiveReturnType = ReturnType<typeof useAgColumnReactive>;
type GetColumnOldHide = RseAgColumnReactiveReturnType['getColumnOldHide'];
type SetColumnOldHide = RseAgColumnReactiveReturnType['setColumnOldHide'];
type RestoreColumnOldHide =
  RseAgColumnReactiveReturnType['restoreColumnOldHide'];

type GetLastColDefsRefOpts = {
  persistColumns: PersistColumns;
  realColDefs: ColDef<any>[];
  getColumnOldHide: GetColumnOldHide;
  setColumnOldHide: SetColumnOldHide;
};

function getLastColDefsRef(ops: GetLastColDefsRefOpts) {
  const { persistColumns, realColDefs, getColumnOldHide, setColumnOldHide } =
    ops;
  const lastColDefsRef = ref(
    mergeLayout(persistColumns, _.cloneDeep(realColDefs), {
      id: (col) => col.field!,
      pinned: (col) => col.pinned || col.field?.startsWith('__'), // 固定不可动
      isShow: (col) => !getColumnOldHide(col), // 是否显示
      setShow: (col, show, p) => {
        col.hide = !show;

        // 设置响应式列的hide原值
        setColumnOldHide(col.field!, !show);

        // 还原宽度
        if (_.isNumber(p.width) && p.width > 0) {
          col.width = p.width;
          col.flex = undefined;
        }
      }
    })
  );
  return lastColDefsRef;
}

type TableCtx = ReturnType<typeof useShareData>;
type FillSettingOps = {
  tableCtx: TableCtx;
  persistColumns: PersistColumns;
  visibles: PlainObject<boolean | 'deleted'>;
  lastColDefs: Ref<any[]>;
  columns: { name: string; title: string }[];
  getColumnApi: () => ColumnApi | undefined;
  setColumnOldHide: SetColumnOldHide;
  restoreColumnOldHide: RestoreColumnOldHide;
  saveName: string;
  realColDefs: ColDef<any>[];
};

function fillSetting(ops: FillSettingOps) {
  const {
    tableCtx,
    persistColumns,
    visibles,
    lastColDefs,
    columns,
    getColumnApi,
    setColumnOldHide,
    restoreColumnOldHide,
    saveName,
    realColDefs
  } = ops;
  for (const e of persistColumns) {
    // 前面已经设置deleted不用设置
    if (visibles[e.id] !== 'deleted') {
      visibles[e.id] = e.show;
    }
  }

  // 提供列配置
  tableCtx.$('visibles')(visibles);

  // 计算固定列数
  let pinnedCount = 0;
  for (const col of lastColDefs.value) {
    if (
      col.pinned == 'left' ||
      col.pinned === true ||
      col.field?.startsWith('__')
    )
      pinnedCount++;
    else break;
  }

  lastColDefs.value
    .filter((e) => !e.pinned && !e.field?.startsWith('__'))
    .forEach((e) => {
      columns.push({
        name: e.field!,
        title: e.headerName!
      });
    });

  tableCtx.$('columns')(columns);

  // 隐藏函数
  tableCtx.$('showColumn')((name: string, visible: boolean, save = true) => {
    if (name) {
      const columnApi = getColumnApi();
      columnApi?.setColumnVisible(name, visible);

      const p = _.find(persistColumns, (e) => e.id == name);
      p!.show = visible;
      visibles[name] = visible;

      // 设置响应式列的hide原值
      setColumnOldHide(name, !visible);
    }

    if (save && saveName) {
      saveLayout('table', ['columns', saveName], persistColumns);
    }
  });

  // 移动列
  tableCtx.$('moveColumn')((name: string, index: number) => {
    const columnApi = getColumnApi();
    columnApi?.moveColumn(name, index + pinnedCount);

    let oldIndex = _.findIndex(persistColumns, (e) => e.id == name);
    arrayMove(persistColumns, oldIndex, index);

    oldIndex = _.findIndex(columns, (e) => e.name == name);
    arrayMove(columns, oldIndex, index);
    if (saveName) {
      saveLayout('table', ['columns', saveName], persistColumns);
    }
  });

  // 还原配置
  tableCtx.$('restore')(() => {
    if (saveName) {
      deleteLayout('table', ['columns', saveName]);
    }

    columns.length = 0;
    persistColumns.length = 0;
    const newColDefs = _.cloneDeep(realColDefs);
    for (const col of newColDefs) {
      if (col.pinned || col.field?.startsWith('__')) continue;

      const field = col.field!;
      // forceHide列不显示
      if (visibles[field] === 'deleted') {
        col.hide = true;
      } else {
        visibles[field] = !col.hide;
        col.hide = col.hide ?? false;
      }

      columns.push({
        name: field,
        title: col.headerName!
      });

      persistColumns.push({
        id: field,
        show: !col.hide
      });
    }

    // 还原oldHide
    restoreColumnOldHide();

    lastColDefs.value = newColDefs;
    const columnApi = getColumnApi();
    nextTick(() => columnApi?.resetColumnState());
  });
}

type UseColumnSettingOps = {
  realColDefs: ColDef<any>[];
  getColumnApi: () => ColumnApi;
  getAgApi: () => GridApi<any>;
  tableName: string;
  registerApi?: boolean;
  moreCloned: GridOptions<any>;
};

/**
 * SearchTable/FormTable/DataTable 三个table组件列排序显示控制
 */
function useTableColumnSetting(ops: UseColumnSettingOps) {
  const {
    realColDefs,
    getColumnApi,
    getAgApi,
    tableName,
    registerApi = true,
    moreCloned
  } = ops;
  // 控制设置抽屉弹窗选中状态
  const visibles: PlainObject<boolean | 'deleted'> = reactive({});
  // field => name映射
  const columns: { name: string; title: string }[] = [];

  // 处理响应式列
  const { getColumnOldHide, setColumnOldHide, restoreColumnOldHide } =
    useAgColumnReactive(realColDefs, moreCloned, visibles, columns);

  const persistColumns = getPersistColumns(tableName, realColDefs);
  const lastColDefs = getLastColDefsRef({
    persistColumns,
    realColDefs,
    getColumnOldHide,
    setColumnOldHide
  });

  let tableCtx: TableCtx | null = null;
  if (registerApi) {
    // 提供 table 访问上下文, 共command使用
    tableCtx = useShareData('table', 'ctx');

    // 给列表配置提供api
    if (tableCtx) {
      // 提供api
      tableCtx.$('api')(getAgApi);
      fillSetting({
        tableCtx,
        persistColumns,
        visibles,
        lastColDefs,
        columns,
        getColumnApi,
        setColumnOldHide,
        restoreColumnOldHide,
        saveName: tableName,
        realColDefs
      });
    }
  }
  appendEventToMoreCloned({
    moreCloned,
    persistColumns,
    visibles,
    tableName,
    columns,
    lastColDefs
  });

  return {
    visibles,
    columns,
    getColumnOldHide,
    setColumnOldHide,
    restoreColumnOldHide,
    persistColumns,
    lastColDefs,
    tableCtx
  };
}

type AppendEventToMoreClonedOps = {
  moreCloned: GridOptions<any>;
  persistColumns: PersistColumns;
  visibles: PlainObject<boolean | 'deleted'>;
  tableName: string;
  columns: { name: string; title: string }[];
  lastColDefs: ReturnType<typeof getLastColDefsRef>;
};
function appendEventToMoreCloned(ops: AppendEventToMoreClonedOps) {
  const {
    moreCloned,
    persistColumns,
    visibles,
    tableName,
    columns,
    lastColDefs
  } = ops;

  const onColumnVisible = moreCloned.onColumnVisible;
  moreCloned.onColumnVisible = (event) => {
    onColumnVisible?.(event);
    if (!event.column) return;
    const name = event.column.getColId();
    const visible = event.visible!;
    const p = _.find(persistColumns, (e) => e.id == name);
    p!.show = visible;
    if (event.source == 'api' || !Object.hasOwn(visibles, name)) {
      return;
    }
    visibles[name] = visible;
    if (tableName) {
      saveLayout('table', ['columns', tableName], persistColumns);
    }
  };

  // 用鼠标拖动列的处理
  const onColumnMoved = moreCloned.onColumnMoved;
  moreCloned.onColumnMoved = (event) => {
    onColumnMoved?.(event);
    if (!event.column) return;
    if (event.source == 'api' || columns.length == 0) {
      return;
    }
    // 左侧固定列数量
    let pinnedLeftCount = 0;
    for (const col of lastColDefs.value) {
      if (
        col.pinned == 'left' ||
        col.pinned === true ||
        col.field?.startsWith('__')
      ) {
        ++pinnedLeftCount;
      }
    }
    const index = event.toIndex! - pinnedLeftCount;
    const name = event.column!.getColId();
    let oldIndex = _.findIndex(persistColumns, (e) => e.id == name);
    arrayMove(persistColumns, oldIndex, index);
    oldIndex = _.findIndex(columns, (e) => e.name == name);
    arrayMove(columns, oldIndex, index);
    if (tableName) {
      saveLayout('table', ['columns', tableName], persistColumns);
    }
  };

  // 列宽度变化
  const onColumnResized = moreCloned.onColumnResized;
  moreCloned.onColumnResized = (event) => {
    onColumnResized?.(event);
    if (!event.column || !event.finished || event.source == 'flex') return;
    if (event.source == 'api' || columns.length == 0) {
      return;
    }
    const width = event.column.getActualWidth();
    const name = event.column!.getColId();
    const col = _.find(persistColumns, (e) => e.id == name);
    if (col) {
      col.width = width;
      // 保存配置
      if (tableName) {
        saveLayout('table', ['columns', tableName], persistColumns);
      }
    }
  };
}

export {
  fillSetting,
  getLastColDefsRef,
  getPersistColumns,
  useTableColumnSetting
};
